/**
 * CSRF Token Storage
 *
 * Stores CSRF token in memory with cookie-based fallback for persistence.
 * Cookie is used instead of sessionStorage to survive:
 * - Mobile WebView restarts (sessionStorage is cleared)
 * - Browser tab discards (Chrome unloads inactive tabs)
 * - Cross-tab scenarios
 *
 * The token is also mirrored to sessionStorage as a secondary fallback.
 *
 * Security Note: The cookie is NOT httpOnly (we need JS to read it),
 * but it's only a CSRF nonce — not a secret credential.
 */

import { getCookieName } from '@/lib/config/api.config';
import { logger } from '@/lib/utils/logger';

const CSRF_TOKEN_KEY = 'csrf_token';
const CSRF_COOKIE_NAME = 'csrf_token_client'; // Non-httpOnly cookie for JS access

// ponytail: pure helper hoisted out of the cookie-split loop (no loop-var capture).
const trimPart = (c: string) => c.trim();

// Module-level variable (private to this module)
let csrfToken: string | null = null;

/**
 * Store CSRF token in memory, cookie, and sessionStorage
 */
export const setCsrfToken = (token: string | null): void => {
  // SSR Safety: Don't cache on server to avoid cross-user state pollution
  if (typeof window === 'undefined') return;

  csrfToken = token;

  if (token) {
    // Primary: Store in a non-httpOnly cookie (survives WebView restarts)
    try {
      const maxAge = 4 * 60 * 60; // 4 hours, matches access token expiry
      document.cookie = `${getCookieName(CSRF_COOKIE_NAME)}=${token}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
    } catch (error) {
      logger.warn('csrfToken', 'Failed to store CSRF token in cookie', { error });
    }

    // Secondary: Also store in sessionStorage
    try {
      sessionStorage.setItem(CSRF_TOKEN_KEY, token);
    } catch (error) {
      logger.warn('csrfToken', 'Failed to store CSRF token in sessionStorage', { error });
    }
  } else {
    // Clear cookie
    try {
      document.cookie = `${getCookieName(CSRF_COOKIE_NAME)}=; Path=/; Max-Age=-1`;
    } catch {
      // Ignore errors
    }

    // Clear sessionStorage
    try {
      sessionStorage.removeItem(CSRF_TOKEN_KEY);
    } catch {
      // Ignore errors
    }
  }
};

/**
 * Retrieve CSRF token from memory, then cookie, then sessionStorage
 */
export const getCsrfToken = (): string | null => {
  // SSR Safety: Don't read cache on server
  if (typeof window === 'undefined') return null;

  // First check memory (fastest)
  if (csrfToken) {
    return csrfToken;
  }

  // Fallback to cookie (survives WebView restarts and tab discards)
  try {
    const cookieName = getCookieName(CSRF_COOKIE_NAME);
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.split('=').map(trimPart);
      if (name === cookieName && value) {
        csrfToken = value; // Restore to memory
        return value;
      }
    }
  } catch {
    // Cookie access might fail in some environments
  }

  // Tertiary fallback: sessionStorage
  try {
    const stored = sessionStorage.getItem(CSRF_TOKEN_KEY);
    if (stored) {
      csrfToken = stored; // Restore to memory
      return stored;
    }
  } catch {
    // sessionStorage might be disabled
  }

  return null;
};

/**
 * Clear CSRF token from memory, cookie, and sessionStorage
 */
export const clearCsrfToken = (): void => {
  csrfToken = null;

  if (typeof window === 'undefined') return;

  // Clear cookie
  try {
    document.cookie = `${getCookieName(CSRF_COOKIE_NAME)}=; Path=/; Max-Age=-1`;
  } catch {
    // Ignore errors
  }

  // Clear sessionStorage
  try {
    sessionStorage.removeItem(CSRF_TOKEN_KEY);
  } catch {
    // Ignore errors
  }
};
